import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  TextInput,
  Alert,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { firestoreService } from '../services/firestoreService';
import { callSubmitOrderRating } from '../services/cloudFunctions';
import { Button } from '../components/Button';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';

/**
 * Calificar un pedido entregado.
 *
 * Reglas: solo el customer del pedido y solo si status === DELIVERED y
 * rated !== true. Lo valida la Cloud Function.
 */
export default function RateOrderScreen() {
  const router = useRouter();
  const { orderId } = useLocalSearchParams<{ orderId: string }>();

  const [order, setOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [restaurantStar, setRestaurantStar] = useState(0);
  const [driverStar, setDriverStar] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    const unsub = firestoreService.listenToFoodOrderById(orderId, (o) => {
      setOrder(o);
      setLoading(false);
    });
    return () => unsub();
  }, [orderId]);

  if (loading) {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </SafeAreaView>
    );
  }

  if (!order) {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="alert-circle-outline" size={56} color={Colors.border} />
        <Text style={{ marginTop: 10, color: Colors.textSecondary }}>Pedido no encontrado.</Text>
      </SafeAreaView>
    );
  }

  if (order.rated === true) {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="checkmark-circle" size={64} color="#4CAF50" />
        <Text style={{ marginTop: 12, fontSize: 17, fontWeight: '700', color: Colors.text }}>
          Ya calificaste este pedido
        </Text>
        <TouchableOpacity onPress={() => volver(router)} style={{ marginTop: 16 }}>
          <Text style={{ color: Colors.primary, fontWeight: '600' }}>Volver</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  if (order.status !== 'DELIVERED') {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="time-outline" size={64} color={Colors.textSecondary} />
        <Text style={{ marginTop: 12, fontSize: 17, fontWeight: '700', color: Colors.text }}>
          Pedido aún no entregado
        </Text>
        <Text style={{ marginTop: 6, color: Colors.textSecondary, textAlign: 'center' }}>
          Podrás calificarlo cuando termine la entrega.
        </Text>
      </SafeAreaView>
    );
  }

  const handleSubmit = async () => {
    if (restaurantStar === 0 && driverStar === 0) {
      Alert.alert('Califica al menos uno', 'Toca las estrellas del restaurante o del repartidor.');
      return;
    }
    setSubmitting(true);
    try {
      await callSubmitOrderRating({
        orderId: orderId!,
        restaurantRating: restaurantStar || undefined,
        driverRating: driverStar || undefined,
        comment: comment.trim() || undefined,
      });
      Alert.alert('¡Gracias!', 'Tu calificación fue registrada.', [
        { text: 'OK', onPress: () => volver(router) },
      ]);
    } catch (e: any) {
      Alert.alert('Error', mensajeDeError(e, 'No pudimos enviar tu calificación. Intenta de nuevo en un momento.'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (value: number, onChange: (v: number) => void) => (
    <View style={styles.starRow}>
      {[1, 2, 3, 4, 5].map((s) => (
        <TouchableOpacity key={s} onPress={() => onChange(s)}>
          <Ionicons
            name={s <= value ? 'star' : 'star-outline'}
            size={36}
            color={s <= value ? '#FF9500' : '#D0D0D0'}
            style={{ marginHorizontal: 4 }}
          />
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="close" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Calificar pedido</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={{ padding: SIZES.padding }}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>¿Cómo estuvo {order.restaurantName ?? 'el restaurante'}?</Text>
          <Text style={styles.muted}>Calidad de la comida y tiempo de preparación.</Text>
          {renderStars(restaurantStar, setRestaurantStar)}
        </View>

        {order.driverId && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>¿Cómo estuvo tu repartidor?</Text>
            <Text style={styles.muted}>Cuidado del pedido, tiempo, amabilidad.</Text>
            {renderStars(driverStar, setDriverStar)}
          </View>
        )}

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Comentario (opcional)</Text>
          <TextInput
            style={styles.commentInput}
            placeholder="Cuéntanos cómo fue tu experiencia"
            value={comment}
            onChangeText={setComment}
            multiline
            maxLength={500}
          />
        </View>

        <Button
          title={submitting ? 'Enviando…' : 'Enviar calificación'}
          onPress={handleSubmit}
          loading={submitting}
          disabled={submitting}
          style={{ marginTop: 8 }}
        />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F8F9FA' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#FFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: { padding: 5 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: Colors.text },
  card: {
    backgroundColor: '#FFF',
    padding: 16,
    borderRadius: SIZES.radius,
    marginBottom: 12,
  },
  cardTitle: { fontSize: 15, fontWeight: '700', color: Colors.text },
  muted: { fontSize: 12, color: Colors.textSecondary, marginTop: 4 },
  starRow: { flexDirection: 'row', justifyContent: 'center', marginTop: 12 },
  commentInput: {
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    color: Colors.text,
    backgroundColor: '#FAFAFA',
    height: 100,
    marginTop: 10,
    textAlignVertical: 'top',
  },
});
